<template>
  <div class="modal-overlay">
    <div class="modal">
      <div class="modal-header">
        <h2>{{ $t('bo.occupationSkills.createOccupationTitle') }}</h2>
        <button @click="$emit('close')" class="btn-close">✕</button>
      </div>

      <form @submit.prevent="handleSubmit" class="modal-form">
        <div class="modal-body">
          <div class="field">
            <label>{{ $t('bo.occupationSkills.createOccupationSubfamily') }} <span class="required">*</span></label>
            <select v-model="form.occupation_subfamily_id" required>
              <option value="">{{ $t('bo.occupationSkills.createOccupationSelectSubfamily') }}</option>
              <optgroup v-for="group in groupedSubfamilies" :key="group.name" :label="group.name">
                <option v-for="sf in group.subfamilies" :key="sf.id" :value="sf.id">{{ sf.name }}</option>
              </optgroup>
            </select>
          </div>

          <div class="field">
            <label>{{ $t('bo.occupationSkills.createOccupationName') }} <span class="required">*</span></label>
            <input v-model="form.name" type="text" required :placeholder="$t('bo.occupationSkills.createOccupationName')" />
          </div>

          <div class="field">
            <label>{{ $t('bo.occupationSkills.createOccupationDescription') }}</label>
            <textarea v-model="form.description" rows="3" />
          </div>

          <div class="field field-inline">
            <label class="checkbox-label">
              <input v-model="form.is_default_suggestion" type="checkbox" />
              {{ $t('bo.occupationSkills.createOccupationIsDefault') }}
            </label>
          </div>

          <div v-if="error" class="error-msg">{{ error }}</div>
        </div>

        <div class="modal-footer">
          <button type="button" @click="$emit('close')" class="btn-cancel">{{ $t('bo.common.cancel') }}</button>
          <button type="submit" class="btn-primary" :disabled="saving">
            {{ saving ? $t('bo.common.saving') : $t('bo.occupationSkills.createOccupation') }}
          </button>
        </div>
      </form>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted } from 'vue'
import { useI18n } from 'vue-i18n'
import occupationSkillService from '@/services/occupationSkillService'

const { t } = useI18n()
const emit = defineEmits(['close', 'created'])

const form = ref({
  occupation_subfamily_id: '',
  name: '',
  description: '',
  is_default_suggestion: false,
})
const saving = ref(false)
const error = ref('')
const subfamilies = ref([])

const groupedSubfamilies = computed(() => {
  const map = {}
  for (const sf of subfamilies.value) {
    const gname = sf.group?.name || 'Autres'
    if (!map[gname]) map[gname] = { name: gname, subfamilies: [] }
    map[gname].subfamilies.push(sf)
  }
  return Object.values(map)
})

onMounted(async () => {
  try {
    const res = await occupationSkillService.getAllSubfamilies()
    subfamilies.value = res.data || []
  } catch {
    // non-critical, select will be empty
  }
})

const handleSubmit = async () => {
  saving.value = true
  error.value = ''
  try {
    await occupationSkillService.createRole({
      occupation_subfamily_id: form.value.occupation_subfamily_id || undefined,
      name: form.value.name,
      description: form.value.description || undefined,
      is_default_suggestion: form.value.is_default_suggestion,
    })
    emit('created')
  } catch (e) {
    error.value = e.response?.data?.message || t('bo.occupationSkills.createOccupationFailed')
  } finally {
    saving.value = false
  }
}
</script>

<style scoped>
.modal-overlay {
  position: fixed; inset: 0; background: rgba(0,0,0,0.5);
  display: flex; align-items: center; justify-content: center;
  z-index: 1000;
}
.modal {
  background: white; border-radius: 0.75rem; width: 100%; max-width: 500px;
  box-shadow: 0 20px 60px rgba(0,0,0,0.2); display: flex; flex-direction: column;
  max-height: 90vh; overflow: hidden;
}
.modal-header {
  display: flex; justify-content: space-between; align-items: center;
  padding: 1.25rem 1.5rem; border-bottom: 1px solid #e5e7eb; flex-shrink: 0;
}
.modal-header h2 { margin: 0; font-size: 1.1rem; font-weight: 700; color: #1f2937; }
.btn-close { background: none; border: none; font-size: 1.1rem; color: #9ca3af; cursor: pointer; }
.btn-close:hover { color: #374151; }

.modal-form { display: flex; flex-direction: column; flex: 1; overflow: hidden; }
.modal-body { padding: 1.5rem; overflow-y: auto; flex: 1; display: flex; flex-direction: column; gap: 1.25rem; }

.field { display: flex; flex-direction: column; gap: 0.35rem; }
.field label { font-size: 0.875rem; font-weight: 600; color: #374151; }
.required { color: #ef4444; }

.field input[type="text"],
.field select,
.field textarea {
  width: 100%; padding: 0.5rem 0.75rem;
  border: 1px solid #d1d5db; border-radius: 0.375rem;
  font-size: 0.875rem; box-sizing: border-box; background: white;
}
.field input:focus, .field select:focus, .field textarea:focus {
  outline: none; border-color: #3b82f6; box-shadow: 0 0 0 3px rgba(59,130,246,0.1);
}
.field textarea { resize: vertical; font-family: inherit; }

.field-inline { flex-direction: row; align-items: center; }
.checkbox-label { display: flex; align-items: center; gap: 0.5rem; font-size: 0.875rem; font-weight: 600; color: #374151; cursor: pointer; }
.checkbox-label input { width: 1rem; height: 1rem; cursor: pointer; }

.error-msg {
  padding: 0.75rem; background: #fef2f2; border: 1px solid #fecaca;
  border-radius: 0.375rem; color: #dc2626; font-size: 0.875rem;
}

.modal-footer {
  display: flex; justify-content: flex-end; gap: 0.75rem;
  padding: 1rem 1.5rem; border-top: 1px solid #e5e7eb; flex-shrink: 0;
}
.btn-cancel {
  padding: 0.5rem 1rem; background: white; border: 1px solid #d1d5db;
  border-radius: 0.375rem; font-size: 0.875rem; color: #374151; cursor: pointer;
}
.btn-cancel:hover { background: #f9fafb; }
.btn-primary {
  padding: 0.5rem 1.25rem; background: #3b82f6; color: white;
  border: none; border-radius: 0.375rem; font-size: 0.875rem; font-weight: 600; cursor: pointer;
}
.btn-primary:hover:not(:disabled) { background: #2563eb; }
.btn-primary:disabled { opacity: 0.6; cursor: not-allowed; }
</style>
